import { useEffect, useMemo, useRef } from 'react'

import { VatSuggestion } from '../../../hooks/useFetchSuggestions'
import { VatSuggestionFormRow, VatSuggestionsForm } from '../utils/formSchema'

interface UseFormValuesProps {
  suggestions: VatSuggestion[] | undefined
  reset: (values: VatSuggestionsForm) => void
  setValue: (name: string, value: VatSuggestionFormRow, options?: { shouldDirty: boolean }) => void
}

/* The hook to manage form values across several pages within table */
/* Old values are preserved as much as we can (ref), new values added to it so you can change state and still bounce back and forth if needed */
export const useFormValues = ({ suggestions, reset, setValue }: UseFormValuesProps) => {
  /* All values from all table pages: ref keeps everything from different pages despite pagination */
  const allValuesRef = useRef<Map<string, VatSuggestionFormRow>>(new Map())
  const formInitializedRef = useRef(false)

  const defaultValues = useMemo<VatSuggestionsForm>(() => {
    if (!suggestions?.length) {
      return {}
    }
    return suggestions.reduce<VatSuggestionsForm>((acc, suggestion) => {
      acc[suggestion.id] = {
        isApproved: suggestion.isApproved,
        isRejected: suggestion.isRejected,
        suggestedTaxRateId: suggestion.suggestedTaxRateId,
      }
      return acc
    }, {})
  }, [suggestions])

  useEffect(() => {
    if (!suggestions?.length || !Object.keys(defaultValues).length) {
      return
    }

    const allValues = allValuesRef.current
    const newSuggestions: string[] = []

    /* default values will change on current page change, so we add them to ref if not already added */
    for (const [id, defaultValue] of Object.entries(defaultValues)) {
      if (!allValues.has(id)) {
        allValues.set(id, defaultValue)
        newSuggestions.push(id)
      }
    }

    /* First load default values init */
    if (!formInitializedRef.current && allValues.size > 0) {
      reset(Object.fromEntries(allValues))
      formInitializedRef.current = true
      return
    }

    /* Add new suggestions to form without resetting existing values */
    if (newSuggestions.length > 0) {
      for (const id of newSuggestions) {
        setValue(id, defaultValues[id], { shouldDirty: false })
      }
    }
  }, [suggestions, defaultValues, setValue, reset])
}
